import { useMemo, useState } from "react";
import {
    Checkbox,
    ConfigProvider,
    InputNumber,
    Segmented,
    Slider,
} from "antd";

// Average prices, all held in GBP and converted for display. These are
// deliberately rough "typical" figures for a mid-range European motorbike
// tour — the page states them openly in the "How the estimate works"
// section so the number reads as a ballpark, not a quote.
const P = {
    camping: 18, // per night, pitch for a small tent
    hotel: { 2: 70, 3: 100, 4: 150, 5: 240 } as Record<number, number>, // per night
    lunch: 14, // per day
    dinner: 28, // per day
    mpg: 55, // motorbike, UK gallons
    fuelPerLitre: 1.45, // petrol, £/L
    crossing: { ferry: 150, tunnel: 190, none: 0 }, // return, bike + rider
};
const L_PER_UK_GALLON = 4.54609;
// Fixed £→€ rate — a calculator this approximate doesn't need a live feed.
const RATE = { GBP: 1, EUR: 1.17 };
const SYMBOL = { GBP: "£", EUR: "€" };

type Accom = "camping" | "hotel";
type Crossing = "ferry" | "tunnel" | "none";
type Currency = "GBP" | "EUR";

const CROSSING_LABEL: Record<Crossing, string> = {
    ferry: "Ferry",
    tunnel: "Le Shuttle",
    none: "Already there",
};

export default function TripCostCalculator() {
    const [days, setDays] = useState(7);
    const [accom, setAccom] = useState<Accom>("hotel");
    const [stars, setStars] = useState(3);
    const [distance, setDistance] = useState(1500);
    const [lunch, setLunch] = useState(true);
    const [dinner, setDinner] = useState(true);
    const [crossing, setCrossing] = useState<Crossing>("tunnel");
    const [currency, setCurrency] = useState<Currency>("GBP");

    const d = Math.max(1, days || 1);
    const dist = Math.max(0, distance || 0);

    const calc = useMemo(() => {
        // One night fewer than the trip length — you ride home on the last
        // day, so a 7-day trip is 6 nights of accommodation.
        const nights = Math.max(0, d - 1);
        const accomPerNight =
            accom === "camping" ? P.camping : P.hotel[stars] ?? P.hotel[3];
        const accomTotal = nights * accomPerNight;
        const litres = dist / P.mpg * L_PER_UK_GALLON;
        const fuelTotal = litres * P.fuelPerLitre;
        const lunchTotal = lunch ? d * P.lunch : 0;
        const dinnerTotal = dinner ? d * P.dinner : 0;
        const crossingTotal = P.crossing[crossing];
        const total =
            accomTotal + fuelTotal + lunchTotal + dinnerTotal + crossingTotal;
        return {
            nights,
            accomPerNight,
            accomTotal,
            litres,
            fuelTotal,
            lunchTotal,
            dinnerTotal,
            crossingTotal,
            total,
            perDay: total / d,
        };
    }, [d, dist, accom, stars, lunch, dinner, crossing]);

    // Convert a GBP figure to the selected currency and format it.
    const money = (gbp: number) =>
        `${SYMBOL[currency]}${Math.round(gbp * RATE[currency]).toLocaleString("en-GB")}`;

    const rows: { label: string; sub: string; value: number }[] = [
        {
            label: "Accommodation",
            sub:
                accom === "camping"
                    ? `${calc.nights} nights camping @ ${money(calc.accomPerNight)}`
                    : `${calc.nights} nights ${stars}★ hotel @ ${money(calc.accomPerNight)}`,
            value: calc.accomTotal,
        },
        {
            label: "Fuel",
            sub: `${dist.toLocaleString("en-GB")} mi · ~${Math.round(calc.litres)} L @ 55 mpg`,
            value: calc.fuelTotal,
        },
        {
            label: "Lunch",
            sub: lunch ? `${d} × ${money(P.lunch)}` : "Self-catering",
            value: calc.lunchTotal,
        },
        {
            label: "Dinner",
            sub: dinner ? `${d} × ${money(P.dinner)}` : "Self-catering",
            value: calc.dinnerTotal,
        },
        {
            label: "Crossing",
            sub: `${CROSSING_LABEL[crossing]}${crossing === "none" ? "" : " · return"}`,
            value: calc.crossingTotal,
        },
    ];

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: "#0f4c5c",
                    borderRadius: 10,
                    fontFamily:
                        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                },
            }}
        >
            <div className="gt-calc">
                <div className="gt-calc__controls">
                    <div className="gt-calc__field">
                        <label className="gt-eyebrow gt-calc__label">
                            How many days?
                        </label>
                        <InputNumber
                            min={1}
                            max={60}
                            value={days}
                            onChange={(v) => setDays(Number(v) || 1)}
                            style={{ width: 120 }}
                            aria-label="Number of days"
                        />
                    </div>

                    <div className="gt-calc__field">
                        <label className="gt-eyebrow gt-calc__label">
                            Where are you sleeping?
                        </label>
                        <Segmented
                            value={accom}
                            onChange={(v) => setAccom(v as Accom)}
                            options={[
                                { label: "Camping", value: "camping" },
                                { label: "Hotel", value: "hotel" },
                            ]}
                        />
                        {accom === "hotel" && (
                            <div style={{ marginTop: 10 }}>
                                <Segmented
                                    value={stars}
                                    onChange={(v) => setStars(Number(v))}
                                    options={[2, 3, 4, 5].map((s) => ({
                                        label: `${s}★`,
                                        value: s,
                                    }))}
                                />
                            </div>
                        )}
                    </div>

                    <div className="gt-calc__field">
                        <label className="gt-eyebrow gt-calc__label">
                            Total distance — {dist.toLocaleString("en-GB")} miles
                        </label>
                        <div className="gt-calc__distance">
                            <Slider
                                min={100}
                                max={5000}
                                step={50}
                                value={dist}
                                onChange={(v) => setDistance(v as number)}
                                style={{ flex: 1 }}
                            />
                            <InputNumber
                                min={0}
                                max={20000}
                                value={distance}
                                onChange={(v) => setDistance(Number(v) || 0)}
                                style={{ width: 96 }}
                                suffix="mi"
                                aria-label="Total distance in miles"
                            />
                        </div>
                    </div>

                    <div className="gt-calc__field">
                        <label className="gt-eyebrow gt-calc__label">
                            Eating out?
                        </label>
                        <div className="gt-calc__checks">
                            <Checkbox
                                checked={lunch}
                                onChange={(e) => setLunch(e.target.checked)}
                            >
                                Lunch
                            </Checkbox>
                            <Checkbox
                                checked={dinner}
                                onChange={(e) => setDinner(e.target.checked)}
                            >
                                Dinner
                            </Checkbox>
                        </div>
                    </div>

                    <div className="gt-calc__field">
                        <label className="gt-eyebrow gt-calc__label">
                            Getting across the Channel
                        </label>
                        <Segmented
                            value={crossing}
                            onChange={(v) => setCrossing(v as Crossing)}
                            options={[
                                { label: "Ferry", value: "ferry" },
                                { label: "Le Shuttle", value: "tunnel" },
                                { label: "Already there", value: "none" },
                            ]}
                        />
                    </div>
                </div>

                <div className="gt-calc__summary">
                    <div className="gt-calc__summary-head">
                        <span className="gt-eyebrow gt-calc__summary-eyebrow">
                            Estimated cost
                        </span>
                        <Segmented
                            size="small"
                            value={currency}
                            onChange={(v) => setCurrency(v as Currency)}
                            options={[
                                { label: "£", value: "GBP" },
                                { label: "€", value: "EUR" },
                            ]}
                        />
                    </div>

                    <div className="gt-calc__total">{money(calc.total)}</div>
                    <div className="gt-calc__perday">
                        about {money(calc.perDay)} per day, for one rider
                    </div>

                    <div className="gt-calc__rows">
                        {rows.map((r) => (
                            <div key={r.label} className="gt-calc__row">
                                <div className="gt-calc__row-main">
                                    <span className="gt-calc__row-label">
                                        {r.label}
                                    </span>
                                    <span className="gt-calc__row-value">
                                        {money(r.value)}
                                    </span>
                                </div>
                                <div className="gt-calc__row-sub">{r.sub}</div>
                            </div>
                        ))}
                    </div>

                    <p className="gt-calc__note">
                        Rough averages for one rider on one bike — a ballpark to
                        plan around, not a quote.
                    </p>
                </div>
            </div>
        </ConfigProvider>
    );
}
